import React, { useMemo } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import InfoRow from 'components/common/InfoRow';
import SubtleBadge from 'components/common/SubtleBadge';

import {
  AWB_REQUIRED_ADDRESS_FIELDS,
  PICKLIST_TYPES,
  PICKLIST_TYPE_LABELS,
} from 'constants/admin/warehouse';

const ADDRESS_FIELD_LABELS = {
  address_name: 'Authorised Person',
  address_phone: 'Phone',
  address_email: 'Email',
  address_line_1: 'Address Line 1',
  address_line_2: 'Address Line 2',
  address_city: 'City',
  address_state: 'State',
  address_country: 'Country',
  address_pincode: 'Pincode',
};

const WarehouseViewMode = ({ warehouse }) => {
  const missingFields = useMemo(() => {
    return AWB_REQUIRED_ADDRESS_FIELDS.filter(field => {
      const value = warehouse?.[field];
      return typeof value === 'string' ? value.trim() === '' : !value;
    });
  }, [warehouse]);

  return (
    <Row>
      {missingFields.length > 0 && (
        <Col md={12}>
          <Alert variant="danger" className="d-flex align-items-start">
            <FontAwesomeIcon
              icon="exclamation-triangle"
              className="me-2 mt-1 flex-shrink-0"
            />
            <div>
              <strong>AWB cannot be issued.</strong> The following required
              fields are missing:{' '}
              {missingFields
                .map(field => ADDRESS_FIELD_LABELS[field])
                .join(', ')}
            </div>
          </Alert>
        </Col>
      )}
      <Col md={3}>
        <InfoRow className="mb-3" label="Name" value={warehouse?.name} />

        <InfoRow
          className="mb-3"
          label="Address Email"
          value={warehouse?.address_email}
        />

        <InfoRow
          className="mb-3"
          label="Address City"
          value={warehouse?.address_city}
        />

        <InfoRow
          className="mb-3"
          label="Logo Image"
          value={
            warehouse?.logo_image ? (
              <img
                src={warehouse.logo_image}
                alt="logo"
                className="img-thumbnail"
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center border rounded bg-light"
                style={{ width: '100px', height: '100px' }}
              >
                <div className="text-center">
                  <FontAwesomeIcon icon="image" className="fs-8" />
                  <p className="mb-0 fs-9">No Image</p>
                </div>
              </div>
            )
          }
        />
      </Col>

      <Col md={3}>
        <InfoRow
          className="mb-3"
          label="Party Name"
          value={warehouse?.party_name}
        />

        <InfoRow
          className="mb-3"
          label="Address Phone"
          value={warehouse?.address_phone}
        />

        <InfoRow
          className="mb-3"
          label="Address State"
          value={warehouse?.address_state}
        />

        <InfoRow
          className="mb-3"
          label="Signature Image"
          value={
            warehouse?.signature_image ? (
              <img
                src={warehouse.signature_image}
                alt="signature"
                className="img-thumbnail"
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center border rounded bg-light"
                style={{ width: '100px', height: '100px' }}
              >
                <div className="text-center">
                  <FontAwesomeIcon icon="image" className="fs-8" />
                  <p className="mb-0 fs-9">No Image</p>
                </div>
              </div>
            )
          }
        />
      </Col>

      <Col md={3}>
        <InfoRow
          className="mb-3"
          label="Authorised Person"
          value={warehouse?.address_name}
        />

        <InfoRow
          className="mb-3"
          label="Address Line 1"
          value={warehouse?.address_line_1}
        />

        <InfoRow
          className="mb-3"
          label="Address Country"
          value={warehouse?.address_country}
        />

        <InfoRow
          className="mb-3"
          label="Status"
          value={
            <SubtleBadge
              bg={warehouse?.inventory_available ? 'success' : 'secondary'}
            >
              {warehouse?.inventory_available ? 'Available' : 'Unavailable'}
            </SubtleBadge>
          }
        />

        <InfoRow
          className="mb-3"
          label="AWB Auto Generate"
          value={
            <SubtleBadge
              bg={warehouse?.is_awb_auto_generate ? 'success' : 'secondary'}
            >
              {warehouse?.is_awb_auto_generate ? 'On' : 'Off'}
            </SubtleBadge>
          }
        />

        <InfoRow
          className="mb-3"
          label="Expire Date Required"
          value={
            <SubtleBadge
              bg={warehouse?.is_expire_date_required ? 'success' : 'secondary'}
            >
              {warehouse?.is_expire_date_required ? 'On' : 'Off'}
            </SubtleBadge>
          }
        />

        <InfoRow
          className="mb-3"
          label="Picklist Pass"
          value={
            <SubtleBadge
              bg={warehouse?.is_picklist_pass_enabled ? 'success' : 'secondary'}
            >
              {warehouse?.is_picklist_pass_enabled ? 'On' : 'Off'}
            </SubtleBadge>
          }
        />

        <InfoRow
          className="mb-3"
          label="Picklist Type"
          value={
            <SubtleBadge
              bg={
                warehouse?.picklist_type === PICKLIST_TYPES.BATCH_CODE
                  ? 'info'
                  : 'secondary'
              }
            >
              {PICKLIST_TYPE_LABELS[warehouse?.picklist_type] ||
                PICKLIST_TYPE_LABELS[PICKLIST_TYPES.BARCODE]}
            </SubtleBadge>
          }
        />

        <InfoRow
          className="mb-3"
          label="Picking Video Recording"
          value={
            <SubtleBadge
              bg={warehouse?.is_picking_video_enabled ? 'success' : 'secondary'}
            >
              {warehouse?.is_picking_video_enabled ? 'On' : 'Off'}
            </SubtleBadge>
          }
        />
      </Col>

      <Col md={3}>
        <InfoRow className="mb-3" label="GSTIN" value={warehouse?.gstin} />

        <InfoRow
          className="mb-3"
          label="Address Line 2"
          value={warehouse?.address_line_2}
        />

        <InfoRow
          className="mb-3"
          label="Address Pincode"
          value={warehouse?.address_pincode}
        />

        <InfoRow
          className="mb-3"
          label="Order SLA Hours"
          value={`${warehouse?.order_sla_hours ?? 72} hours`}
        />
      </Col>
    </Row>
  );
};

export default WarehouseViewMode;
